import { Link } from "react-router-dom";
import { HelpCircle, ArrowLeft } from "lucide-react";
import { CustomerFooter } from "@/components/CustomerFooter";
import { FAQChatBot } from "@/components/FAQChatBot";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="container py-4">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm">Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 container py-12">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <HelpCircle className="w-8 h-8 text-primary" />
              <h1 className="text-3xl font-bold text-foreground">Frequently Asked Questions</h1>
            </div>
            <p className="text-muted-foreground">
              Everything you need to know about Shakatah
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="what-is" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What is Shakatah?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Shakatah is a group gifting and split payment platform that helps friends, family, and colleagues pool money for gifts, events, and shared purchases. Merchants can also integrate our widget to offer group payment options directly on their stores.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="how-works" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How does split payment work?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                When you find a product you want to buy as a group, the organizer creates a split payment request and invites contributors. Each person pays their share, and once fully funded, the order is placed automatically. The organizer can track progress and send reminders.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="fees-customer" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Is it free for customers?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! Creating events, organizing group gifts, and making contributions is completely free for customers. There are no hidden fees for participants.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="fees-merchant" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What are the fees for merchants?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We offer flexible pricing for merchants including a free tier for small businesses. Our paid plans start with a 3-month free trial. Check our <Link to="/pricing" className="text-primary hover:underline">pricing page</Link> for details.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="platforms" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Which e-commerce platforms are supported?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We currently support Shopify with easy widget installation. WooCommerce, Magento, and custom integrations are coming soon. Service professionals can use our platform without any e-commerce integration.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="payment-methods" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What payment methods are supported?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                We support UPI, cards, net banking, and wallets through Razorpay for Indian transactions. For international payments, we integrate with Stripe. Organizers can also accept manual payments like bank transfers or cash.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="security" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Is my payment information secure?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Absolutely. We never store your payment details. All transactions are processed through PCI-DSS compliant payment gateways (Razorpay/Stripe) with bank-grade encryption.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="refunds" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                What happens if a group purchase is cancelled?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                If a split payment doesn't reach its target by the deadline, all contributors receive automatic refunds. For event cancellations, the organizer can initiate refunds. See our <Link to="/refund-policy" className="text-primary hover:underline">refund policy</Link> for details.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="professionals" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                Can service professionals use Shakatah?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                Yes! Tutors, coaches, therapists, and other professionals can use Shakatah to collect recurring payments from clients. Set up payment schedules, send automatic reminders, and track all payments in one dashboard.
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="help" className="border rounded-lg px-4">
              <AccordionTrigger className="text-left font-medium">
                How do I get help?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">
                You can reach us at <a href="mailto:support@shakatah.com" className="text-primary hover:underline">support@shakatah.com</a> or visit our <Link to="/contact" className="text-primary hover:underline">contact page</Link>. We typically respond within 24 hours.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </main>

      <CustomerFooter tagline="Smart group gifting & payments" />
      <FAQChatBot context="customer" />
    </div>
  );
};

export default FAQ;
